// @see https://cloudinary.com/documentation/resizing_and_cropping#control_gravity
import type { Responsive } from '#types/common'
import type { BackgroundAuto, ImageTransformationsAlignment } from '#types/imageTransformations'
import breakpoints from '#core/design-system/breakpoints.json'

export interface ImageTransformations {
  /** The (compass) direction to align the image to. */
  align?: ImageTransformationsAlignment
  alignFill?: ImageTransformationsAlignment
  /** A background property used in tandem with align (c_pad) if the image is a different aspect ratio than presets  */
  backgroundAuto?: BackgroundAuto
  /** The cropping method to use. */
  crop?: 'fill' | 'scale'
  /** Whether or not to include the default 2.0 dpr  */
  excludeDPR?: boolean
  /** The format of the image. */
  format?: 'auto' | 'auto:image' | 'png'
  /** The height of the image, in pixels. */
  height?: number
  quality?: number | 'auto'
  /** Sets both the width and height at the same time. */
  size?: number
  /**
   * Applying sharpness and dpr to fix blurry images in TNF Nora PLP and PDP images
   * reference: https://digital.vfc.com/jira/browse/GLOBAL15-103845
   */
  sharpen?: number
  /** The width of the image, in pixels. */
  width?: number
  unSharpMask?: number
}

export interface ResponsiveImageTransformations extends Omit<ImageTransformations, 'height' | 'width' | 'size'> {
  height?: Responsive<number>
  size?: Responsive<number>
  width?: Responsive<number>
}

export const defaultTransformationTokens: string[] = []

export const transformationTokenMap: Record<keyof Omit<ImageTransformations, 'excludeDPR'>, string> = {
  align: 'c_pad,g_',
  alignFill: 'c_fill,g_',
  backgroundAuto: 'b_auto:',
  crop: 'c_',
  format: 'f_',
  height: 'h_',
  quality: 'q_',
  sharpen: 'e_sharpen:',
  size: 'w_',
  unSharpMask: 'e_unsharp_mask:',
  width: 'w_',
}

const DEFAULT_NAMED_TRANSFORMATION = 't_img'

/**
 * A helper function for extracting the transformation values from the consumer-provided options.
 * @param t The transformations to apply to the image.
 * @returns {string[]} An array of transformation values (e.g. ['f_auto', 'c_scale', 'w_650']).
 * @category Utils
 */
const generateTransformationValues = (t: ImageTransformations): string[] => {
  const transformationValues = new Set(defaultTransformationTokens)
  const hasRedundantDimensions = t.size && (t.height || t.width)

  /* If we do not receive an override format, will use auto by default */
  t.format ??= 'auto'
  t.sharpen ??= 60

  // Sharpen and unSharpMask options do not coexist. So, setting the sharpen to 0 when unSharpMask is present
  if (t.unSharpMask) t.sharpen = 0

  if (hasRedundantDimensions) {
    delete t.height
    delete t.width
    log.debug('[@domains/core/utils/getImageTransformations] Both a size and height/width value was set. Defaulting to size.')
  }

  Object.entries(transformationTokenMap).forEach(([key, token]) => {
    if (t[key] && key !== 'excludeDPR') {
      const value = `${token}${t[key]}`
      transformationValues.add(value)
    }
  })

  return [...transformationValues]
}

/**
 * Applies the appropriate Cloudinary transformations to a given image URL.
 * @param url URL used to construct new URL object.
 * @param transformations The transformations to apply to the image.
 * @example
 * Images will arrive in the following formats:
 * 'https://assets.timberland.eu/images/t_Thumbnail/v1702342099/TB0A27PQ001-HERO/Brooklyn-Hiker-for-Men-in-Black.png'
 *
 * The default named transformation (e.g. t_Thumbnail) will be replaced with t_img, followed by a set of transformations.
 * For example, if the image above was resized to a width of 650px, the URL would be transformed to:
 * 'https://assets.timberland.eu/images/t_img/f_auto,c_scale,w_650/v1702342099/TB0A27PQ001-HERO/Brooklyn-Hiker-for-Men-in-Black.png'
 * @category Utils
 * @see https://cloudinary.com/documentation/transformation_reference
 * @returns {string} The transformed image URL.
 */

export const getImageTransformations = (url: string = '', transformations: ImageTransformations = {}): string => {
  if (!url) {
    log.debug('[@domains/core/utils/getImageTransformations] Please provide a url.')
    return ''
  }

  if (url.includes(DEFAULT_NAMED_TRANSFORMATION)) {
    log.debug('[@domains/core/utils/getImageTransformations] No op. The transformations have already been applied.')
    return url
  }
  try {
    const baseUrl = new URL(url)
    const urlPathValues = baseUrl.pathname.split('/')

    let _: string, ___: string, path: string | undefined, type: string
    let rest: string[] = []

    if (urlPathValues.includes('images', 1) || urlPathValues.includes('videos', 1)) {
      [_, type, ___, ...rest] = urlPathValues
    }
    else if (urlPathValues.includes('upload', 2)) {
      [_, type, path, ...rest] = urlPathValues
    }
    else {
      log.debug('[@domains/core/utils/getImageTransformations] The URL does not match the expected format.')
      return url
    }

    if (type === 'videos') transformations.format ??= 'auto:image'

    // Applying 2x image size to all DPR levels for increased quality of the image
    // reference for details: https://digital.vfc.com/jira/browse/GLOBAL15-91223
    // Adds option to remove this dpr size vie excludeDPR boolean
    // reference for details: https://digital.vfc.com/jira/browse/GLOBAL15-104413
    baseUrl.pathname = `${type}${path ? `/${path}` : ''}/${DEFAULT_NAMED_TRANSFORMATION}/${[...generateTransformationValues(transformations)].join(',')}${transformations.excludeDPR ? '' : '/dpr_2.0'}/${rest.join('/')}`

    return decodeURIComponent(`${baseUrl}`)
  }
  catch (error: any) {
    log.error(`[@domains/core/utils/getImageTransformations] Unable to transform the provided URL: ${url}.`, error)
    return ''
  }
}

export const getResponsiveImageTransformations = (url: string = '', t: ResponsiveImageTransformations = {}) =>
  Object.keys(breakpoints).reduce((sources, breakpoint) => {
    const width = t.width?.[breakpoint]
    const height = t.height?.[breakpoint]
    const size = t.size?.[breakpoint]
    const align = t?.align
    const alignFill = t?.alignFill
    const unSharpMask = t?.unSharpMask
    const sharpen = t?.sharpen
    const backgroundAuto = t?.backgroundAuto
    const excludeDPR = t?.excludeDPR

    if ((width && height) || size) {
      sources[breakpoint] = getImageTransformations(url, {
        align,
        alignFill,
        width,
        height,
        size,
        sharpen,
        backgroundAuto,
        unSharpMask,
        excludeDPR
      })
    }

    return sources
  }, {} as Responsive<string>)
